/**
 * .gsx v2 (Gaussian Splat eXperimental) format spec.
 *
 * Layout:
 *   [Header: HEADER_SIZE bytes]
 *   [Block index table: blockCount * BLOCK_INDEX_ENTRY_SIZE bytes]
 *   [Octree node table: nodeCount * OCTREE_NODE_SIZE bytes]
 *   [Compressed blocks: nối liền nhau, offset/size theo index table]
 *
 * Per block (sau khi giải gzip wrapper):
 *   [N Huffman tables + entropy-coded streams cùng thứ tự sau Hilbert sort]
 *
 * Octree gives LOD pyramid: each node owns 1 block, internal nodes hold
 * subsampled splats representing their descendants (opacity-weighted).
 * Leaves hold the full-detail splats. Runtime traversal selects nodes per
 * frame based on frustum + screen-space error.
 */

export const GSX_MAGIC = 0x31585347; // "GSX1" little-endian
export const GSX_VERSION = 3;

export const HEADER_SIZE = 96;
export const BLOCK_INDEX_ENTRY_SIZE = 48;
export const OCTREE_NODE_SIZE = 72;

export const FLAG_HUFFMAN = 1 << 0;
export const FLAG_GZIP = 1 << 1;

export const NO_BLOCK = 0xffffffff;
export const NO_NODE = -1;

export interface QuantParams {
  positionBits: number;
  shDcBits: number;
  scaleBits: number;
  rotationBits: number;
  opacityBits: number;
}

export interface GsxHeader {
  magic: number;
  version: number;
  flags: number;
  splatCount: number;
  blockCount: number;
  blockSize: number;
  nodeCount: number;
  rootNodeId: number;
  aabbMin: [number, number, number];
  aabbMax: [number, number, number];
  quant: QuantParams;
}

export interface GsxBlockIndexEntry {
  offset: number;
  sizeCompressed: number;
  sizeUncompressed: number;
  aabbMin: [number, number, number];
  aabbMax: [number, number, number];
}

/**
 * A grid cell at some LOD level. Re-uses the OctreeNode shape (kept the name
 * for now) but the grid build leaves parent/child as NO_NODE — cells are flat,
 * organized by `level`. Cull iterates cells and picks one per region whose
 * level matches the current screen-space-error threshold.
 */
export interface OctreeNode {
  blockId: number;
  parentId: number;
  childIds: [number, number, number, number, number, number, number, number];
  aabbMin: [number, number, number];
  aabbMax: [number, number, number];
  /** LOD level (0 = root cell covering full scene; deeper = finer detail). */
  level: number;
}

export interface GsxMeta {
  header: GsxHeader;
  blocks: GsxBlockIndexEntry[];
  nodes: OctreeNode[];
  /** Optional: direct S3 URL of the .gsx file so client can Range-fetch blocks
   * without going through the Hono server. Server populates this when the
   * bucket has public-read configured. */
  blockUrl?: string;
}

export interface SplatBlock {
  splatCount: number;
  positions: Float32Array;
  shDc: Float32Array;
  scales: Float32Array;
  rotations: Float32Array;
  opacities: Float32Array;
  aabbMin: [number, number, number];
  aabbMax: [number, number, number];
}

export function writeHeader(view: DataView, h: GsxHeader): void {
  view.setUint32(0, h.magic, true);
  view.setUint16(4, h.version, true);
  view.setUint16(6, h.flags, true);
  view.setUint32(8, h.splatCount, true);
  view.setUint32(12, h.blockCount, true);
  view.setUint32(16, h.blockSize, true);
  view.setFloat32(20, h.aabbMin[0], true);
  view.setFloat32(24, h.aabbMin[1], true);
  view.setFloat32(28, h.aabbMin[2], true);
  view.setFloat32(32, h.aabbMax[0], true);
  view.setFloat32(36, h.aabbMax[1], true);
  view.setFloat32(40, h.aabbMax[2], true);
  view.setUint8(44, h.quant.positionBits);
  view.setUint8(45, h.quant.shDcBits);
  view.setUint8(46, h.quant.scaleBits);
  view.setUint8(47, h.quant.rotationBits);
  view.setUint8(48, h.quant.opacityBits);
  view.setUint32(52, h.nodeCount, true);
  view.setInt32(56, h.rootNodeId, true);
}

export function readHeader(view: DataView): GsxHeader {
  const magic = view.getUint32(0, true);
  if (magic !== GSX_MAGIC) {
    throw new Error(`Bad magic: 0x${magic.toString(16)} (expected 0x${GSX_MAGIC.toString(16)})`);
  }
  return {
    magic,
    version: view.getUint16(4, true),
    flags: view.getUint16(6, true),
    splatCount: view.getUint32(8, true),
    blockCount: view.getUint32(12, true),
    blockSize: view.getUint32(16, true),
    aabbMin: [view.getFloat32(20, true), view.getFloat32(24, true), view.getFloat32(28, true)],
    aabbMax: [view.getFloat32(32, true), view.getFloat32(36, true), view.getFloat32(40, true)],
    quant: {
      positionBits: view.getUint8(44),
      shDcBits: view.getUint8(45),
      scaleBits: view.getUint8(46),
      rotationBits: view.getUint8(47),
      opacityBits: view.getUint8(48),
    },
    nodeCount: view.getUint32(52, true),
    rootNodeId: view.getInt32(56, true),
  };
}

export function writeOctreeNode(view: DataView, off: number, n: OctreeNode): void {
  view.setUint32(off + 0, n.blockId, true);
  view.setInt32(off + 4, n.parentId, true);
  for (let i = 0; i < 8; i++) view.setInt32(off + 8 + i * 4, n.childIds[i]!, true);
  view.setFloat32(off + 40, n.aabbMin[0], true);
  view.setFloat32(off + 44, n.aabbMin[1], true);
  view.setFloat32(off + 48, n.aabbMin[2], true);
  view.setFloat32(off + 52, n.aabbMax[0], true);
  view.setFloat32(off + 56, n.aabbMax[1], true);
  view.setFloat32(off + 60, n.aabbMax[2], true);
  view.setUint32(off + 64, n.level, true);
  // 68..71 reserved
}

export function readOctreeNode(view: DataView, off: number): OctreeNode {
  const childIds: OctreeNode["childIds"] = [-1, -1, -1, -1, -1, -1, -1, -1];
  for (let i = 0; i < 8; i++) childIds[i] = view.getInt32(off + 8 + i * 4, true);
  return {
    blockId: view.getUint32(off + 0, true),
    parentId: view.getInt32(off + 4, true),
    childIds,
    aabbMin: [
      view.getFloat32(off + 40, true),
      view.getFloat32(off + 44, true),
      view.getFloat32(off + 48, true),
    ],
    aabbMax: [
      view.getFloat32(off + 52, true),
      view.getFloat32(off + 56, true),
      view.getFloat32(off + 60, true),
    ],
    level: view.getUint32(off + 64, true),
  };
}

export function writeBlockIndexEntry(view: DataView, off: number, e: GsxBlockIndexEntry): void {
  // 48-byte block index entry layout:
  //   [ 0..7]   offset      f64
  //   [ 8..11]  sizeComp    u32
  //   [12..15]  sizeUncomp  u32
  //   [16..27]  aabbMin     f32 × 3   ← was f16 before, lost precision for
  //   [28..39]  aabbMax     f32 × 3     world coords > ~10 units (e.g. real
  //   [40..47]  reserved                Gaussian Splat scenes can be 100s of
  //                                     units, where f16 had ~0.24 unit step).
  view.setFloat64(off + 0, e.offset, true);
  view.setUint32(off + 8, e.sizeCompressed, true);
  view.setUint32(off + 12, e.sizeUncompressed, true);
  view.setFloat32(off + 16, e.aabbMin[0], true);
  view.setFloat32(off + 20, e.aabbMin[1], true);
  view.setFloat32(off + 24, e.aabbMin[2], true);
  view.setFloat32(off + 28, e.aabbMax[0], true);
  view.setFloat32(off + 32, e.aabbMax[1], true);
  view.setFloat32(off + 36, e.aabbMax[2], true);
}

export function readBlockIndexEntry(view: DataView, off: number): GsxBlockIndexEntry {
  return {
    offset: view.getFloat64(off + 0, true),
    sizeCompressed: view.getUint32(off + 8, true),
    sizeUncompressed: view.getUint32(off + 12, true),
    aabbMin: [
      view.getFloat32(off + 16, true),
      view.getFloat32(off + 20, true),
      view.getFloat32(off + 24, true),
    ],
    aabbMax: [
      view.getFloat32(off + 28, true),
      view.getFloat32(off + 32, true),
      view.getFloat32(off + 36, true),
    ],
  };
}

// Float16 helpers (IEEE 754 half-precision) — kept as utility for any future
// use; block index entry no longer uses these.
function float32ToFloat16(val: number): number {
  const buf = new ArrayBuffer(4);
  const f32 = new Float32Array(buf);
  const u32 = new Uint32Array(buf);
  f32[0] = val;
  const x = u32[0]!;
  const sign = (x >>> 16) & 0x8000;
  let mant = x & 0x7fffff;
  let exp = (x >>> 23) & 0xff;
  if (exp === 0xff) {
    return sign | 0x7c00 | (mant ? 0x200 : 0);
  }
  exp = exp - 127 + 15;
  if (exp >= 31) return sign | 0x7c00;
  if (exp <= 0) {
    if (exp < -10) return sign;
    mant = (mant | 0x800000) >>> (1 - exp);
    return sign | (mant >>> 13);
  }
  return sign | (exp << 10) | (mant >>> 13);
}

function float16ToFloat32(h: number): number {
  const sign = (h & 0x8000) << 16;
  const exp = (h & 0x7c00) >>> 10;
  const mant = h & 0x03ff;
  let bits: number;
  if (exp === 0) {
    if (mant === 0) {
      bits = sign;
    } else {
      // subnormal
      let m = mant;
      let e = -14;
      while ((m & 0x400) === 0) {
        m <<= 1;
        e -= 1;
      }
      m &= 0x3ff;
      bits = sign | ((e + 127) << 23) | (m << 13);
    }
  } else if (exp === 31) {
    bits = sign | 0x7f800000 | (mant << 13);
  } else {
    bits = sign | ((exp - 15 + 127) << 23) | (mant << 13);
  }
  const buf = new ArrayBuffer(4);
  new Uint32Array(buf)[0] = bits >>> 0;
  return new Float32Array(buf)[0]!;
}
